import { optionalOutcome, participationOutcome } from './grading-mode.js';
import { resolveMatchedBranch, resolveEffectiveOptions, activeFeedbackForBranch } from './dropdown.js';
import { feedbackForScenarioRow, pickScenarioBranch, resolveScenarioOptions } from './scenario.js';
// ─── Authoring-time warnings ────────────────────────────────────────────────────
/** MCQ's counterpart to Dropdown's `branchRoleIssue`, extended for `allowMultiple`: single-select
 * still requires exactly one correct option per branch (`'multiple-correct'` is a real setup
 * mistake there, same reasoning as Dropdown — evaluateMCQ's correctness check would have no single
 * winner to terminate on... except MCQ's isCorrect is actually a set-match, so in practice a
 * single-select branch with >1 correct just makes an unreachable combination unless the UI also
 * enforces single-select semantics on the branch role-click handler, which it does). Multi-select
 * has no upper bound — "every option correct" is a legitimate "select all" setup, not a bug — so
 * only the zero-correct case (structurally unreachable in every mode) is checked there. */
export function mcqBranchRoleIssue(branch, options, allowMultiple) {
    const correctCount = options.filter(o => branch.roles[o.id] === 'correct').length;
    if (correctCount === 0)
        return 'none-correct';
    if (!allowMultiple && correctCount > 1)
        return 'multiple-correct';
    return null;
}
/**
 * The targeted states this selection fires — the ONE matcher, used by every grading mode.
 *
 * Lifted out of the graded path unchanged rather than reimplemented, so survey and optional fire
 * exactly what graded would for the same selection. A second copy that merely agreed today is the
 * failure this shape exists to prevent.
 *
 * `withCorrectness` is false in the participation modes, and it gates the ROLE checks — not the
 * triggers themselves:
 *
 *   - **`_absent`** says "this option was not selected", which is a plain fact about a
 *     submission. What makes it a correctness idea in graded is the RESTRICTION layered on it —
 *     it applies only to correct-roled options, so it reads as "you left out a required answer".
 *     Drop the restriction and the trigger still means something: *you did not take the rope*.
 *     So outside graded it fires for ANY unselected option.
 *   - **the `role !== 'correct'` guard** on a present option, which exists so a targeted state
 *     can never override a correct answer. Outside graded no role is consulted at all, so every
 *     selected option's state fires.
 *
 * ## The arithmetic to know about
 *
 * Present and absent are COMPLEMENTARY: every option is in exactly one of them. Graded never
 * notices, because the roles keep the two sets disjoint — absent is confined to correct options
 * and present excludes them, so only a couple can ever fire. Without roles that limiter is gone,
 * and a block with both authored on all N options fires exactly N states on every submission.
 * That is arithmetic, not a defect, and it is the teacher's to manage — the same call the product
 * already made when it chose parallel per-option feedback over exact-pattern combo states.
 *
 * A blank does not land here at all: an empty selection takes the `blankTargeted` path in
 * optional and the `unanswered` state in survey, so "picked nothing" never fires every absent
 * state at once.
 */
function matchedTargeted(states, options, selectedSet, withCorrectness, allowMultiple) {
    const optionRoles = new Map(options.map(o => [o.id, o.role]));
    return states.filter(s => {
        if (s.type !== 'targeted')
            return false;
        if (s.trigger.endsWith('_absent')) {
            // Multi-select only, in EVERY mode: a single pick cannot omit an option while still being
            // a pick, so there is nothing for the trigger to describe. Switching multi-select off used
            // to leave absent states firing — and their cards on screen — in graded.
            if (!allowMultiple)
                return false;
            const id = s.trigger.slice('option_'.length, -'_absent'.length);
            if (!selectedSet.has(id))
                return !withCorrectness || optionRoles.get(id) === 'correct';
            return false;
        }
        if (s.trigger.startsWith('option_')) {
            const id = s.trigger.slice('option_'.length);
            if (!selectedSet.has(id))
                return false;
            return withCorrectness ? optionRoles.get(id) !== 'correct' : true;
        }
        return false;
    });
}
/**
 * Returns the feedback states that match a student's answer.
 *
 * Evaluation sequence:
 * 1. optional mode → any_response, answered or not — leaving it blank is just as acceptable an
 *    outcome as picking something, so unlike graded/survey there's no separate unanswered state
 *    carved out for this mode, and no correctness check either.
 * 2. Empty selection (graded/survey only) → unanswered state
 * 3. survey mode → any_answer (any selection counts as participation, no correctness involved)
 * 4. graded mode → correct is checked first and is terminal; only when the answer
 *    is not correct does targeted feedback get consulted, falling back to `incorrect`
 *
 * `correct`/`any_answer`/`any_response` are three distinct trigger names for what's conceptually
 * "the one positive-outcome state," not the same trigger reused across modes — see
 * positiveTriggerForMode in use-input-block-feedback.ts for why a raw `correct_any` string
 * firing in survey mode ("answered something," no correctness involved) shouldn't share a name
 * with graded's "matched the correct answer."
 *
 * Correct-first-terminal means targeted feedback can never override a correct answer —
 * not even a stale state left over from a role change, and not even one attached to a
 * neutral option that happens to also be selected. This is why neutral options are
 * excluded from the correctness check below: in a multi-select answer, neutral options
 * can be freely included or omitted without affecting correctness (unlike incorrect
 * options, which always disqualify). A neutral option's targeted feedback can still
 * fire, but only when the answer is otherwise not correct — never alongside `correct`.
 *
 * Targeted trigger rules:
 *   option_X        fires when option X is selected (a non-correct option was chosen)
 *   option_X_absent fires when correct option X is NOT selected (required answer missing)
 *
 * `resolvedVariables` resolves `conditionalBranches` — optional, and a no-op whenever
 * `blockData.conditionalBranches` is unset, which is every MCQ that doesn't use this feature.
 * Identical contract to `evaluateDropdown`'s own `resolvedVariables` param: when a branch
 * resolves, its OWN feedback is what fires (not the block's top-level `feedback`); branches are
 * consulted ONLY in `graded` mode; when branching is on but nothing resolves (missing/invalid
 * equation, or a coverage gap), every option is forced to `role: 'neutral'`
 * (`resolveEffectiveOptions`'s fail-closed fallback) rather than falling back to static roles —
 * see that function's own doc comment in dropdown.ts for why. This holds for both `allowMultiple`
 * modes with no special-casing needed here: the `isCorrect` set-match below is already
 * structurally `false` once `correctIdSet` is emptied by the fail-closed fallback.
 */
export function evaluateMCQ(answer, blockData, resolvedVariables = {}, 
/**
 * The leader's variant in force for this attempt — see `evaluateDropdown`'s own note.
 *
 * A parameter rather than a field because it is RUN-TIME context, exactly like
 * `resolvedVariables`: the same stored block grades differently per attempt depending on what
 * the task drew. Resolve it with `scenarioInForce` first, so a stale id lands on the same
 * variant the leader is showing.
 */
scenarioId) {
    const { selectedIds } = answer;
    const { gradingMode, feedback, conditionalEquation, conditionalBranches, scenarioBranches, angleMode, allowMultiple } = blockData;
    /*
     * A FOLLOWER'S SCENARIO ROW REPLACES the numeric branch, never joins it. The two are separate
     * selectors and a block answers to one; linking disables numeric branching exactly as it
     * disables versions. Checked by presence rather than by a flag, the same way `variableKind`
     * reads a variable's shape — two field signatures cannot disagree with themselves.
     */
    const followed = gradingMode === 'graded' && !!scenarioBranches && scenarioBranches.length > 0;
    const scenarioRow = followed ? pickScenarioBranch(scenarioBranches, scenarioId) : null;
    const matchedBranch = !followed && gradingMode === 'graded'
        ? resolveMatchedBranch(conditionalEquation, conditionalBranches, resolvedVariables, angleMode)
        : null;
    const options = gradingMode !== 'graded' ? blockData.options
        : followed ? resolveScenarioOptions(blockData.options, scenarioBranches, scenarioId, resolvedVariables, angleMode ?? 'degrees')
            : resolveEffectiveOptions(blockData.options, conditionalEquation, conditionalBranches, resolvedVariables, angleMode);
    // Incomplete branch coverage falls back to the top-level set with targeted states STRIPPED, so an
    // unmatched answer always lands on Default Incorrect instead of a leftover targeted state firing
    // terminally on an option that was just declared neutral. See activeFeedbackForBranch.
    /* A row may branch on a comparison of its own, and the branch it lands on may speak for
       itself — "the river is faster, so you drift left" is not what the other branch of the same
       scenario wants to say. Innermost first, then the row, then the block. */
    const scenarioFeedback = scenarioRow
        ? feedbackForScenarioRow(scenarioRow, resolvedVariables, angleMode ?? 'degrees') : undefined;
    const activeFeedback = activeFeedbackForBranch(scenarioFeedback ?? matchedBranch?.feedback, feedback, gradingMode === 'graded'
        && (followed || (!!conditionalBranches && conditionalBranches.length > 0)));
    if (gradingMode === 'optional') {
        // OPTIONAL is INERT: the catch-all, whatever the student did. See `targetedFiresIn`.
        return optionalOutcome(activeFeedback.states);
    }
    if (selectedIds.length === 0) {
        const unanswered = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'unanswered');
        return unanswered ? [unanswered] : [];
    }
    if (gradingMode === 'survey') {
        return participationOutcome(activeFeedback.states, 'survey', matchedTargeted(activeFeedback.states, options, new Set(selectedIds), false, !!allowMultiple));
    }
    // Graded: correct is checked first and is terminal — see doc comment above.
    const selectedSet = new Set(selectedIds);
    const correctIdSet = new Set(options.filter(o => o.role === 'correct').map(o => o.id));
    const incorrectIdSet = new Set(options.filter(o => o.role === 'incorrect').map(o => o.id));
    const isCorrect = correctIdSet.size > 0 &&
        [...correctIdSet].every(id => selectedSet.has(id)) &&
        [...selectedSet].every(id => !incorrectIdSet.has(id));
    if (isCorrect) {
        const correct = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'correct');
        return correct ? [correct] : [];
    }
    // Not correct: fire all matching targeted feedback states (parallel — multiple can fire at once).
    const matched = matchedTargeted(activeFeedback.states, options, selectedSet, true, !!allowMultiple);
    if (matched.length > 0)
        return matched;
    const incorrect = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'incorrect');
    return incorrect ? [incorrect] : [];
}
//# sourceMappingURL=mcq.js.map